// TEMPORARY: remove after test-data phase (docs/runbooks/temp-data-wipe.md).
//
// Setup → Danger zone (admins, staging and dev only): "Wipe all SOC data". Step 1 emails a 6-digit code to the
// signed-in admin's own address; step 2 takes the code and the typed phrase. The server enforces all of it (admin role,
// environment, code, expiry, attempts, rate limit); this screen only asks.

import { useEffect, useState } from 'react';
import { api, errorText, type TempDataWipeInfo, type TempDataWipeRun } from '../../api.ts';
import { Icon } from '../../components/Icon.tsx';
import { Card, ErrorBox, Loading } from '../../components/ui.tsx';
import { useLoad } from '../../hooks.ts';

/** US Eastern, as in the code email. */
const et = (iso: string) => `${new Date(iso).toLocaleString('en-US', { timeZone: 'America/New_York', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', second: '2-digit' })} ET`;

const total = (r: Record<string, number> | undefined) => Object.values(r ?? {}).reduce((a, b) => a + b, 0);

function RunSummary({ run }: { run: TempDataWipeRun }) {
  const tables = run.tables ? total(run.tables.tables) + total(run.tables.siteRows) : null;
  const blobs = run.blobs ? Object.entries(run.blobs.prefixes) : [];
  return (
    <div className="small">
      <div>
        Started {et(run.startedAt)} by <b>{run.by.upn}</b>.
      </div>
      <div>{run.databaseWipedAt ? `Database wiped ${et(run.databaseWipedAt)}: ${tables} rows deleted.` : run.error ? `Failed, nothing deleted: ${run.error}` : 'Database: in progress.'}</div>
      <div>
        Stored files: <b>{run.objects}</b>
        {run.objectsDoneAt && ` (${et(run.objectsDoneAt)})`}
        {run.blobs && !run.blobs.supported && ': this object store cannot delete; see the runbook.'}
      </div>
      {blobs.length > 0 && (
        <table>
          <thead>
            <tr>
              <th>Prefix</th>
              <th>Deleted</th>
              <th>Refused</th>
            </tr>
          </thead>
          <tbody>
            {blobs.map(([p, r]) => (
              <tr key={p}>
                <td className="mono">{p}</td>
                {'error' in r ? (
                  <td colSpan={2} className="mono">
                    {r.error}
                  </td>
                ) : (
                  <>
                    <td>{r.deleted}</td>
                    <td title={r.firstErrors.join('\n')}>{r.failed}</td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function Lists({ info }: { info: TempDataWipeInfo }) {
  return (
    <details>
      <summary>Exactly what is wiped and what is kept</summary>
      <div className="small">
        <p>
          <b>Deleted:</b> <span className="mono">{info.wipes.tables.join(', ')}</span>
        </p>
        <p>
          <b>Per-site rows deleted (global rows kept):</b> <span className="mono">{info.wipes.siteRows.join(', ')}</span>
        </p>
        <p>
          <b>Links to wiped data cleared (rows kept):</b> <span className="mono">{info.wipes.siteLinks.join(', ')}</span>
        </p>
        <p>
          <b>Stored files deleted:</b> <span className="mono">{info.wipes.objectPrefixes.join(' ')}</span>
        </p>
        <p>
          <b>Kept:</b> <span className="mono">{info.keeps.join(', ')}</span>; stored files under <span className="mono">{info.keepsObjectPrefixes.join(' ')}</span>.
        </p>
      </div>
    </details>
  );
}

export function DangerZone() {
  const load = useLoad((signal) => api.tempDataWipe(signal), []);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [sent, setSent] = useState<{ sentTo: string; expiresAt: string } | null>(null);
  const [code, setCode] = useState('');
  const [phrase, setPhrase] = useState('');
  const info = load.data;
  const running = info?.lastRun?.objects === 'running' || info?.lastRun?.objects === 'pending';

  // While stored files are being deleted, refresh every few seconds.
  useEffect(() => {
    if (!running) return;
    const t = setInterval(load.reload, 4000);
    return () => clearInterval(t);
  }, [running, load.reload]);

  if (load.error) return <ErrorBox error={load.error} onRetry={load.reload} />;
  if (!info) return <Loading />;
  if (!info.available) {
    return (
      <div className="state empty">
        <Icon name="lock" /> The temporary data wipe is not available in this environment.
      </div>
    );
  }

  const pending = sent ?? (info.pending ? { sentTo: info.email, expiresAt: info.pending.expiresAt } : null);
  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    setErr(null);
    try {
      await fn();
    } catch (e) {
      setErr(errorText(e));
    } finally {
      setBusy(false);
      load.reload();
    }
  };

  return (
    <div className="danger-zone">
      <Card title={<>Danger zone: {info.environment}</>}>
        <div className="warnbox denied">
          <b>Wipe all SOC data</b>
          <span>
            Temporary, for the test-data phase. Deletes events, alarms, media, communications and recordings, the master data replicated from the ERP (reload it from the
            ERP afterwards), per-site setup, ERP sync queues and caches, and the stored files. Keeps users, roles, settings, global setup and the audit log. It does not touch
            the ERP. It cannot be undone.
          </span>
          <Lists info={info} />
        </div>

        <ol className="small">
          <li>
            <div className="row-wrap">
              <span>
                Email a 6-digit code to <b>{info.email}</b> (your own address; it expires in 10 minutes).
              </span>
              <button type="button" className="btn sm" disabled={busy || running} onClick={() => void run(async () => setSent(await api.tempDataWipeRequest()))}>
                <Icon name="mail" /> {pending ? 'Send a new code' : 'Wipe all SOC data'}
              </button>
            </div>
            {pending && (
              <div className="muted">
                Code sent to {pending.sentTo}; it expires at {et(pending.expiresAt)}
                {info.pending && `, ${info.pending.attemptsLeft} tries left`}.
              </div>
            )}
          </li>
          <li>
            <div className="row-wrap">
              <label className="field inline">
                <span>Code from the email</span>
                <input className="mono" inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} disabled={!pending || busy} />
              </label>
              <label className="field grow">
                <span>
                  Type <b className="mono">{info.phrase}</b>
                </span>
                <input value={phrase} onChange={(e) => setPhrase(e.target.value)} disabled={!pending || busy} autoComplete="off" spellCheck={false} />
              </label>
              <button
                type="button"
                className="btn danger"
                disabled={busy || running || code.length !== 6 || phrase.trim() !== info.phrase}
                onClick={() =>
                  void run(async () => {
                    await api.tempDataWipeConfirm(code, phrase.trim());
                    setSent(null);
                    setCode('');
                    setPhrase('');
                  })
                }
              >
                <Icon name="trash" /> Wipe now
              </button>
            </div>
          </li>
        </ol>
        {err && <ErrorBox error={err} title="Not done" />}
      </Card>

      {info.lastRun && (
        <Card title="Last wipe">
          <RunSummary run={info.lastRun} />
        </Card>
      )}
    </div>
  );
}
